/**
 * OpenHands — AI software development agent
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { Badge, Card, CardContent, PageHeader, Select, StatusDot } from '../../components/ui';
import {
  AlertTriangle,
  Bot,
  ChevronDown,
  ChevronUp,
  Download,
  ExternalLink,
  FolderOpen,
  Globe,
  Key,
  Loader2,
  Play,
  RefreshCw,
  RotateCcw,
  Settings2,
  Square,
  Terminal,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { themedConfirm } from '../../lib/themed-dialogs';

// ── Types ─────────────────────────────────────────────────────────────────────

interface OhStatus {
  ok: boolean;
  status: 'running' | 'stopped' | 'missing' | 'pulling';
  url: string | null; // http://server_ip:port
  external_url: string | null; // https://domain (přes NPM)
  container: string;
  start_error: string | null;
  config: {
    port: number;
    workdir: string;
    llm_provider: string;
    llm_model: string;
    has_api_key: boolean;
    npm_domain: string;
  };
}

interface OhConfig {
  ok: boolean;
  config: {
    port: number;
    workdir: string;
    llm_provider: string;
    llm_model: string;
    llm_api_key: string;
    llm_base_url: string;
    runtime_image: string;
    has_api_key: boolean;
    npm_domain: string;
  };
}

// ── LLM Presets ───────────────────────────────────────────────────────────────

const LLM_PRESETS = [
  {
    id: 'claude-opus',
    label: 'Claude Opus 4.5 (Anthropic)',
    provider: 'anthropic',
    model: 'claude-opus-4-5',
    base_url: '',
  },
  {
    id: 'claude-sonnet',
    label: 'Claude Sonnet 4.5 (Anthropic)',
    provider: 'anthropic',
    model: 'claude-sonnet-4-5',
    base_url: '',
  },
  {
    id: 'gb10-coder',
    label: 'gb10 — Qwen2.5-Coder-32B (vLLM)',
    provider: 'openai',
    model: 'openai/Qwen2.5-Coder-32B-Instruct-AWQ',
    base_url: 'https://10.118.200.18:8000/v1',
  },
  {
    id: 'gb10-qwen3',
    label: 'gb10 — Qwen3-Coder-30B (llama.cpp)',
    provider: 'openai',
    model: 'openai/qwen3-coder-30b',
    base_url: 'https://10.118.200.18:8004/v1',
  },
  {
    id: 'ai2-32b',
    label: 'ai2 — Ollama qwen2.5-coder:32b',
    provider: 'ollama',
    model: 'ollama/qwen2.5-coder:32b',
    base_url: 'https://10.118.200.19:11434',
  },
  { id: 'custom', label: '', provider: 'custom', model: '', base_url: '' },
];

// ── Settings Panel ────────────────────────────────────────────────────────────

const DEFAULT_NPM_DOMAIN = 'openhands.services.maxcloud.cz';

function SettingsPanel({ onSaved }: { onSaved: () => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { data } = useQuery({
    queryKey: ['openhands-config'],
    queryFn: () => api.mcp<{ ok: boolean; config: OhConfig }>('openhands.config', { action: 'get' }).then((r) => r.config as OhConfig),
    enabled: open,
  });
  const cfg = data?.config;
  const [form, setForm] = useState<Record<string, string>>({});
  const [presetId, setPresetId] = useState('claude-opus');
  const [proxyPending, setProxyPending] = useState(false);

  const val = (k: string) =>
    (k in form
      ? form[k]
      : (cfg?.[k as keyof typeof cfg] as string | number | boolean | undefined)?.toString() ??
        '') as string;
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const saveMut = useMutation({
    mutationFn: () => api.mcp('openhands.config', { action: 'set', ...form }),
    onSuccess: () => {
      toast.success(t('openhands.configSaved'));
      setForm({});
      onSaved();
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const handlePreset = (id: string) => {
    setPresetId(id);
    const p = LLM_PRESETS.find((x) => x.id === id);
    if (!p || id === 'custom') return;
    setForm((f) => ({
      ...f,
      llm_provider: p.provider,
      llm_model: p.model,
      llm_base_url: p.base_url,
    }));
  };

  const inp =
    'w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500';

  return (
    <Card>
      <CardContent className='p-4'>
        <button
          onClick={() => setOpen((o) => !o)}
          className='flex items-center justify-between w-full text-xs font-semibold text-gray-400 uppercase tracking-wide hover:text-white transition-colors'
        >
          <span className='flex items-center gap-2'>
            <Settings2 size={13} /> {t('openhands.settingsTitle')}
          </span>
          {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        </button>

        {open && cfg && (
          <div className='mt-5 space-y-5'>
            {/* LLM */}
            <div>
              <div className='text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3'>
                LLM Backend
              </div>
              <div className='space-y-3'>
                <div>
                  <label className='text-xs text-gray-500 mb-1 block'>Preset</label>
                  <Select
                    value={presetId}
                    onChange={(e) => handlePreset(e.target.value)}
                  >
                    {LLM_PRESETS.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.label || t('openhands.presetCustom')}
                      </option>
                    ))}
                  </Select>
                </div>
                <div className='grid grid-cols-2 gap-3'>
                  <div>
                    <label className='text-xs text-gray-500 mb-1 block'>Model</label>
                    <input
                      value={val('llm_model')}
                      onChange={set('llm_model')}
                      className={inp}
                      placeholder='claude-opus-4-5'
                    />
                  </div>
                  <div>
                    <label className='text-xs text-gray-500 mb-1 block'>
                      <Key size={10} className='inline mr-1' />{t('openhands.apiKeyLabel')}
                    </label>
                    <input
                      value={val('llm_api_key')}
                      onChange={set('llm_api_key')}
                      type='password'
                      className={inp}
                      placeholder={cfg.has_api_key
                        ? t('openhands.apiKeySetPlaceholder')
                        : 'sk-ant-...'}
                    />
                  </div>
                </div>
                <div>
                  <label className='text-xs text-gray-500 mb-1 block'>
                    {t('openhands.baseUrlLabel')}
                  </label>
                  <input
                    value={val('llm_base_url')}
                    onChange={set('llm_base_url')}
                    className={inp}
                    placeholder='https://10.118.200.18:8000/v1'
                  />
                </div>
              </div>
            </div>

            {/* Workspace */}
            <div>
              <div className='text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3'>
                <FolderOpen size={11} className='inline mr-1' />Workspace
              </div>
              <div className='grid grid-cols-2 gap-3'>
                <div>
                  <label className='text-xs text-gray-500 mb-1 block'>
                    {t('openhands.workdirLabel')}
                  </label>
                  <input
                    value={val('workdir')}
                    onChange={set('workdir')}
                    className={inp}
                    placeholder='/opt/acm'
                  />
                </div>
                <div>
                  <label className='text-xs text-gray-500 mb-1 block'>Port</label>
                  <input
                    value={val('port')}
                    onChange={set('port')}
                    type='number'
                    className={inp}
                    placeholder='7683'
                  />
                </div>
              </div>
              <div className='mt-2 text-[11px] text-gray-600'>
                {t('openhands.workdirHint')}
              </div>
            </div>

            {/* Runtime image */}
            <div>
              <label className='text-xs text-gray-500 mb-1 block'>Runtime image</label>
              <input
                value={val('runtime_image')}
                onChange={set('runtime_image')}
                className={inp}
                placeholder='ghcr.io/openhands/agent-server:1.15.0-python'
              />
              <div className='text-[11px] text-gray-600 mt-1'>
                {t('openhands.runtimeImageHint')}
              </div>
            </div>

            {/* Veřejný přístup — NPM proxy */}
            <div className='pt-2 border-t border-gray-800'>
              <div className='text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-2'>
                <Globe size={11} /> {t('openhands.publicAccessTitle')}
              </div>
              {cfg?.npm_domain
                ? (
                  <div className='flex items-center gap-3'>
                    <div className='flex-1 text-xs text-gray-300'>
                      <span className='text-gray-500'>{t('openhands.domainLabel')}</span>
                      <a
                        href={`https://${cfg.npm_domain}`}
                        target='_blank'
                        rel='noopener'
                        className='text-blue-400 hover:underline font-mono'
                      >
                        {cfg.npm_domain}
                      </a>
                    </div>
                    <button
                      onClick={async () => {
                        if (!(await themedConfirm(t('openhands.confirmUnpublish', { domain: cfg.npm_domain })))) {
                          return;
                        }
                        setProxyPending(true);
                        try {
                          await api.mcp('openhands.proxy', { action: 'unpublish' });
                          toast.success(t('openhands.proxyDeleted'));
                          onSaved();
                        } catch (e) {
                          toast.error((e as Error).message);
                        } finally {
                          setProxyPending(false);
                        }
                      }}
                      disabled={proxyPending}
                      className='px-3 py-1.5 text-xs rounded-lg border border-red-800/50 text-red-400 hover:bg-red-900/20 disabled:opacity-40 transition-colors'
                    >
                      {proxyPending
                        ? <Loader2 size={11} className='inline animate-spin' />
                        : t('openhands.removeButton')}
                    </button>
                  </div>
                )
                : (
                  <div className='space-y-2'>
                    <div className='text-[11px] text-gray-600'>
                      {t('openhands.publicAccessInactiveHint')}
                    </div>
                    <div className='flex gap-2'>
                      <input
                        id='npm-domain-input'
                        defaultValue={DEFAULT_NPM_DOMAIN}
                        className={`${inp} flex-1`}
                        placeholder={DEFAULT_NPM_DOMAIN}
                      />
                      <button
                        onClick={async () => {
                          const domain =
                            (document.getElementById('npm-domain-input') as HTMLInputElement)?.value
                              ?.trim();
                          if (!domain) {
                            toast.error(t('openhands.enterDomainError'));
                            return;
                          }
                          if (!(await themedConfirm(t('openhands.confirmPublish', { domain })))) return;
                          setProxyPending(true);
                          try {
                            const r = await api.mcp<
                              { ok: boolean; error?: string; external_url?: string }
                            >(
                              'openhands.proxy',
                              { action: 'publish', domain, websocket: true },
                            );
                            if (r.ok) {
                              toast.success(t('openhands.proxyCreated', { url: r.external_url }));
                              onSaved();
                            } else toast.error(r.error ?? t('openhands.genericError'));
                          } catch (e) {
                            toast.error((e as Error).message);
                          } finally {
                            setProxyPending(false);
                          }
                        }}
                        disabled={proxyPending}
                        className='px-3 py-1.5 text-xs rounded-lg border border-gray-700 text-gray-300 hover:text-white hover:border-gray-500 disabled:opacity-40 transition-colors whitespace-nowrap'
                      >
                        {proxyPending
                          ? <Loader2 size={11} className='inline animate-spin mr-1' />
                          : <Globe size={11} className='inline mr-1' />}
                        {t('openhands.publishExternallyButton')}
                      </button>
                    </div>
                  </div>
                )}
            </div>

            {/* Akce */}

            <div className='flex gap-2 pt-2 border-t border-gray-800'>
              <div className='flex-1' />
              <button
                onClick={() => {
                  setForm({});
                  setOpen(false);
                }}
                className='px-3 py-1.5 text-xs text-gray-500 hover:text-white transition-colors'
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={() => saveMut.mutate()}
                disabled={saveMut.isPending || Object.keys(form).length === 0}
                className='px-4 py-1.5 text-xs rounded-lg bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white font-medium transition-colors'
              >
                {saveMut.isPending
                  ? <Loader2 size={11} className='inline animate-spin mr-1' />
                  : null}
                {t('common.save')}
              </button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Logs Panel ────────────────────────────────────────────────────────────────

function LogsPanel() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [lines, setLines] = useState(100);
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['openhands-logs', lines],
    queryFn: () => api.mcp<{ ok: boolean; logs: string }>('openhands.logs', { lines }),
    enabled: open,
  });

  return (
    <Card>
      <CardContent className='p-4'>
        <button
          onClick={() => setOpen((o) => !o)}
          className='flex items-center justify-between w-full text-xs font-semibold text-gray-400 uppercase tracking-wide hover:text-white transition-colors'
        >
          <span className='flex items-center gap-2'>
            <Terminal size={13} /> {t('openhands.containerLogsTitle')}
          </span>
          {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        </button>
        {open && (
          <div className='mt-3 space-y-2'>
            <div className='flex justify-end gap-2'>
              <Select
                value={lines}
                onChange={(e) => setLines(Number(e.target.value))}
              >
                {[50, 100, 200, 500].map((n) => (
                  <option key={n} value={n}>{t('openhands.linesCount', { count: n })}</option>
                ))}
              </Select>
              <button
                onClick={() => refetch()}
                className='p-1.5 text-gray-600 hover:text-white transition-colors'
              >
                <RefreshCw size={12} />
              </button>
            </div>
            {isLoading
              ? (
                <div className='py-4 text-center text-gray-500 text-sm'>
                  <Loader2 size={12} className='inline animate-spin mr-1' />{t('common.loading')}
                </div>
              )
              : (
                <pre className='bg-gray-950 border border-gray-800 rounded-xl p-4 text-xs font-mono text-gray-300 overflow-x-auto max-h-72 overflow-y-auto whitespace-pre-wrap'>
                  {data?.logs ?? t('openhands.emptyLogs')}
                </pre>
              )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function OpenHandsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['openhands-status'],
    queryFn: () => api.mcp<OhStatus>('openhands.status'),
    // Při pullování refreshuj každé 3 s, jinak každých 10 s
    refetchInterval: (q) => q.state.data?.status === 'pulling' ? 3_000 : 10_000,
  });

  const status = data?.status ?? 'missing';
  const running = status === 'running';
  const pulling = status === 'pulling';
  const cfg = data?.config;

  const startMut = useMutation({
    mutationFn: () =>
      api.mcp<{ ok: boolean; status?: string; message?: string; error?: string }>(
        'openhands.start',
      ),
    onSuccess: (r) => {
      if (!r.ok) {
        toast.error(r.error ?? t('openhands.startError'));
        return;
      }
      if (r.status === 'pulling') toast.info(t('openhands.pullingImageToast'));
      else toast.success(t('openhands.startedToast'));
      qc.invalidateQueries({ queryKey: ['openhands-status'] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const stopMut = useMutation({
    mutationFn: () => api.mcp('openhands.stop'),
    onSuccess: () => {
      toast.success(t('openhands.stoppedToast'));
      qc.invalidateQueries({ queryKey: ['openhands-status'] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const restartMut = useMutation({
    mutationFn: () => api.mcp('openhands.restart'),
    onSuccess: () => {
      toast.info(t('openhands.restartStartedToast'));
      qc.invalidateQueries({ queryKey: ['openhands-status'] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  // StatusDot stav
  const dotStatus = running
    ? 'running'
    : pulling
    ? 'pending'
    : status === 'stopped'
    ? 'stopped'
    : 'error';
  const badgeVariant = running ? 'success' : pulling ? 'warning' : 'gray';
  const badgeLabel = running
    ? t('openhands.statusRunning')
    : pulling
    ? t('openhands.statusPulling')
    : status === 'stopped'
    ? t('openhands.statusStopped')
    : t('openhands.statusMissing');

  return (
    <div className='space-y-5'>
      <PageHeader
        title='OpenHands'
        description={t('openhands.pageDescription')}
        icon={Bot}
      />

      {/* Status karta */}
      <Card>
        <CardContent className='p-5'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-4'>
              <div
                className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  running
                    ? 'bg-green-500/10 border border-green-500/20'
                    : pulling
                    ? 'bg-yellow-500/10 border border-yellow-500/20'
                    : 'bg-gray-800 border border-gray-700'
                }`}
              >
                {pulling
                  ? <Loader2 size={22} className='text-yellow-400 animate-spin' />
                  : <Bot size={22} className={running ? 'text-green-400' : 'text-gray-500'} />}
              </div>
              <div>
                <div className='flex items-center gap-2 mb-1'>
                  <span className='font-semibold text-white'>OpenHands Agent</span>
                  <StatusDot status={dotStatus} />
                  <Badge variant={badgeVariant}>{badgeLabel}</Badge>
                </div>
                {pulling && (
                  <div className='text-xs text-yellow-400/80'>
                    {t('openhands.pullingStatusHint')}
                  </div>
                )}
                {!pulling && cfg && (
                  <div className='flex items-center gap-3 text-xs text-gray-500'>
                    <span className='flex items-center gap-1'>
                      <FolderOpen size={10} />
                      {cfg.workdir}
                    </span>
                    <span>port {cfg.port}</span>
                    <span className='font-mono'>{cfg.llm_model}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Akce */}
            <div className='flex items-center gap-2'>
              {running && (data?.external_url || data?.url) && (
                <a
                  href={data.external_url ?? data.url!}
                  target='_blank'
                  rel='noopener'
                  className='flex items-center gap-1.5 px-4 py-2 rounded-xl bg-blue-600/20 border border-blue-500/30 text-blue-400 hover:bg-blue-600/30 text-sm font-medium transition-colors'
                >
                  <ExternalLink size={13} />
                  {data.external_url
                    ? t('openhands.openButton')
                    : t('openhands.openOnPortButton', { port: cfg?.port })}
                </a>
              )}
              {(running || pulling) && (
                <button
                  onClick={() => restartMut.mutate()}
                  disabled={restartMut.isPending || pulling}
                  className='p-2 rounded-lg border border-gray-700 text-gray-400 hover:text-white disabled:opacity-40 transition-colors'
                  title='Restart'
                >
                  {restartMut.isPending
                    ? <Loader2 size={14} className='animate-spin' />
                    : <RotateCcw size={14} />}
                </button>
              )}
              {(running || pulling)
                ? (
                  <button
                    onClick={() => stopMut.mutate()}
                    disabled={stopMut.isPending}
                    className='flex items-center gap-1.5 px-4 py-2 rounded-xl bg-red-600/20 border border-red-500/30 text-red-400 hover:bg-red-600/30 text-sm font-medium disabled:opacity-40 transition-colors'
                  >
                    {stopMut.isPending
                      ? <Loader2 size={13} className='animate-spin' />
                      : <Square size={13} />} {t('openhands.stopButton')}
                  </button>
                )
                : (
                  <button
                    onClick={() => startMut.mutate()}
                    disabled={startMut.isPending}
                    className='flex items-center gap-1.5 px-4 py-2 rounded-xl bg-green-600/20 border border-green-500/30 text-green-400 hover:bg-green-600/30 text-sm font-medium disabled:opacity-40 transition-colors'
                  >
                    {startMut.isPending
                      ? <Loader2 size={13} className='animate-spin' />
                      : <Play size={13} />} {t('openhands.startButton')}
                  </button>
                )}
            </div>
          </div>

          {/* Chyba posledního startu */}
          {data?.start_error && !pulling && (
            <div className='mt-4 flex items-start gap-2 text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2'>
              <XCircle size={12} className='mt-0.5 shrink-0' />
              <pre className='font-mono whitespace-pre-wrap break-all'>{data.start_error}</pre>
            </div>
          )}

          {/* Warning — chybí API klíč */}
          {cfg && !cfg.has_api_key && cfg.llm_provider !== 'ollama' && !pulling && (
            <div className='mt-4 flex items-center gap-2 text-xs text-yellow-400 bg-yellow-500/10 border border-yellow-500/20 rounded-lg px-3 py-2'>
              <AlertTriangle size={12} /> {t('openhands.missingApiKeyWarning')}
            </div>
          )}

          {/* Info — image se stahuje */}
          {status === 'missing' && !isLoading && !data?.start_error && (
            <div className='mt-4 flex items-center gap-2 text-xs text-gray-400 bg-gray-800/50 border border-gray-700 rounded-lg px-3 py-2'>
              <Download size={12} />{' '}
              {t('openhands.imageNotDownloadedHint')}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Nastavení */}
      <SettingsPanel onSaved={() => qc.invalidateQueries({ queryKey: ['openhands-status'] })} />

      {/* Logy */}
      <LogsPanel />
    </div>
  );
}
